/**
 * Product Gift Card Manager - Moon Moon Agency
 * Gestion du formulaire carte cadeau :
 *  - Sélection du variant (montant) avec mise à jour du CTA
 *  - Toggle du bloc "envoi direct au destinataire"
 *  - Synchronisation des line item properties vers le formulaire Shopify
 */

/*********************************************************************************************
 * 🎁 GIFT CARD MANAGER
 */
const GiftCardManager = {
	config: {
		selectors: {
			form: '#product-form-gift-card',
			variantIdInput: '#gift-card-variant-id',
			variantBtns: '[data-variant-id]',
			btnPrice: '[data-btn-price]',
			sendCheckbox: '[data-toggle="gift-card-contact"]',
			contactSection: '#gift-card-contact',
			fromInput: '#gift-card-from',
			toInput: '#gift-card-to',
			recipientEmail: '#gift-card-recipient-email',
			message: '#gift-card-message',
		},
		classes: {
			variantSelected: 'mm-product-form-gift-card__variant--selected',
		},
	},

	init() {
		this._form = window.getElement(this.config.selectors.form);
		if (!this._form) return;

		this._variantIdInput = window.getElement(this.config.selectors.variantIdInput);
		this._btnPrice = window.getElement(this.config.selectors.btnPrice);
		this._sendCheckbox = window.getElement(this.config.selectors.sendCheckbox);
		this._contactSection = window.getElement(this.config.selectors.contactSection);
		this._productUrl = this._form.dataset.productUrl || window.location.pathname;
		this._sectionId = this._form.dataset.sectionId;
		this._cache = new Map();

		this._bindEvents();
		window.log('info', '🎁 [GIFT_CARD]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const variantBtn = e.target.closest('[data-variant-id]');
			if (variantBtn) {
				this._selectVariant(variantBtn);
			}
		});

		if (this._sendCheckbox) {
			this._sendCheckbox.addEventListener('change', () => {
				this._toggleContactSection(this._sendCheckbox.checked);
			});
		}

		this._form.addEventListener('submit', () => {
			this._syncProperties();
		});
	},

	_selectVariant(btn) {
		const allVariants = document.querySelectorAll(this.config.selectors.variantBtns);

		allVariants.forEach((v) => {
			v.classList.remove(this.config.classes.variantSelected);
			v.setAttribute('aria-pressed', 'false');
		});

		btn.classList.add(this.config.classes.variantSelected);
		btn.setAttribute('aria-pressed', 'true');

		const variantId = btn.dataset.variantId;

		if (this._variantIdInput) {
			this._variantIdInput.value = variantId;
		}

		this._updateURL(variantId);
		this._fetchAndReload(variantId);
		window.log('info', '🎁 [GIFT_CARD]', 'Variant selected', { variantId });
	},

	/**
	 * Reflète le montant choisi dans l'URL (comme la fiche produit standard) : lien partageable, rechargement sur le même montant
	 */
	_updateURL(variantId) {
		try {
			const url = new URL(window.location.href);
			url.searchParams.set('variant', variantId);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', '🎁 [GIFT_CARD]', 'Failed to update URL', { error: error.message });
		}
	},

	async _fetchAndReload(variantId) {
		if (!this._sectionId) {
			window.log('warn', '🎁 [GIFT_CARD]', 'No section ID — cannot reload elements');
			return;
		}

		const url = `${this._productUrl}?variant=${variantId}&section_id=${this._sectionId}`;

		if (this._cache.has(url)) {
			ProductManager.updateReloadElements(this._cache.get(url));
			return;
		}

		try {
			const response = await fetch(url);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const html = new DOMParser().parseFromString(await response.text(), 'text/html');
			this._cache.set(url, html);
			ProductManager.updateReloadElements(html);
			window.log('info', '🎁 [GIFT_CARD]', 'Reload complete', { variantId });
		} catch (error) {
			window.log('error', '🎁 [GIFT_CARD]', 'Fetch failed', { error: error.message });
		}
	},

	_toggleContactSection(isChecked) {
		if (!this._contactSection) return;

		if (isChecked) {
			this._contactSection.removeAttribute('hidden');
			this._contactSection.setAttribute('aria-hidden', 'false');
		} else {
			this._contactSection.setAttribute('hidden', '');
			this._contactSection.setAttribute('aria-hidden', 'true');
		}

		window.log('info', '🎁 [GIFT_CARD]', 'Contact section toggled', { isChecked });
	},

	_syncProperties() {
		const recipientEmailInput = window.getElement(this.config.selectors.recipientEmail);
		const messageInput = window.getElement(this.config.selectors.message);

		if (this._sendCheckbox && !this._sendCheckbox.checked) {
			if (recipientEmailInput) recipientEmailInput.disabled = true;
			if (messageInput) messageInput.disabled = true;
		} else {
			if (recipientEmailInput) recipientEmailInput.disabled = false;
			if (messageInput) messageInput.disabled = false;
		}
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => GiftCardManager.init());
} else {
	GiftCardManager.init();
}
